import axios from 'axios';

import { GLTF2Export } from '@babylonjs/serializers/glTF';

import { saveAs } from 'file-saver';

import {
    TransformNode,
    Vector3,
} from '@babylonjs/core';

// ThreeJS USDZ export.

import { GLTFLoader as ThreeGLTFLoader } from './GLTFLoader';
import { USDZExporter as ThreeUSDZExporter } from './UsdzExporter';

export default {
    methods: {
        captureArPreview() {
            if (this.scene) {
                this.stopRenderLoop();

                let textureId = 0;
                let materialId = 0;
                let meshId = 0;

                this.scene.textures.forEach((texture) => {
                    textureId += 1;

                    texture.name = `texture-${textureId}-${(texture.name || '').replace(/[^a-zA-Z0-9]/g, '')}`;

                    // Hack...
                    // eslint-disable-next-line no-underscore-dangle
                    texture._mimeType = 'image/jpeg';
                });

                this.scene.meshes.forEach((mesh) => {
                    meshId += 1;

                    mesh.name = `mesh-${meshId}-${(mesh.name || '').replace(/[^a-zA-Z0-9]/g, '')}`;
                });

                this.scene.materials.forEach((material) => {
                    // eslint-disable-next-line
                    console.log(`Exporting material ${material.name} of ${material.getClassName()} class`);

                    materialId += 1;

                    material.name = `material-${materialId}-${(material.name || '').replace(/[^a-zA-Z0-9]/g, '')}`;
                });

                const options = {
                    shouldExportNode: (node) => {
                        // Export all transform and empty nodes to retain the hierarchy.
                        if (node.constructor && node.constructor.name === 'TransformNode') {
                            return true;
                        }

                        if (node.emptyParent) {
                            return true;
                        }

                        let placementNode = null;

                        let searchNode = node;
                        while (searchNode) {
                            if (searchNode.customPlacementKey) {
                                placementNode = searchNode;
                                break;
                            } else {
                                searchNode = searchNode.parent;
                            }
                        }

                        if (placementNode) {
                            const placementConfig = this.placements[placementNode.customPlacementKey];

                            if (placementConfig == null) {
                                return false;
                            }

                            const placement = placementConfig.fullPlacement;
                            if (placement == null) {
                                return false;
                            }

                            if (placement && placement.custom && placement.custom['three-ar'] === 'true') {
                                // eslint-disable-next-line
                                console.log(`Will export ${node.name} because of ${placementNode.customPlacementKey}`);
                                return true;
                            }
                        }

                        return false;
                    },
                };

                const roots = [...this.scene.rootNodes];
                const reparented = [];

                // Temporarily reparent meshes.
                const root = new TransformNode('arRoot', this.scene);

                roots.forEach((n) => {
                    // eslint-disable-next-line
                    console.log(n.constructor.name);

                    if (n.constructor.name === 'TransformNode' || n.constructor.name === 'Mesh') {
                        n.parent = root;

                        reparented.push(n);
                    }
                });

                // eslint-disable-next-line
                console.log(`${reparented.length} root nodes to be exported`);

                if (this.controller.blueprint.custom['three-ar-center-world'] === 'true') {
                    // Recenter exportable meshes in world coordinates.
                    let worldMin = null;
                    let worldMax = null;

                    this.scene.meshes.forEach((mesh) => {
                        if (options.shouldExportNode(mesh)) {
                            const bb = mesh.getBoundingInfo();
                            const min = bb.boundingBox.minimumWorld;
                            const max = bb.boundingBox.maximumWorld;

                            if (worldMin == null) {
                                worldMin = min;
                            } else {
                                worldMin.minimizeInPlace(min);
                            }

                            if (worldMax == null) {
                                worldMax = max;
                            } else {
                                worldMax.maximizeInPlace(max);
                            }
                        }
                    });

                    if (worldMax != null && worldMin != null) {
                        const center = worldMax.add(worldMin).multiplyByFloats(0.5, 1, 0.5);

                        const offset = new Vector3(-center.x, -center.y, -center.z);

                        root.position = offset;
                    }
                }

                GLTF2Export.GLBAsync(this.scene, 'preview', options).then((gltf) => {
                    this.logMessage({
                        level: 'debug',
                        message: 'Done exporting GLB',
                    });

                    root.position = new Vector3(0, 0, 0);

                    // Unparent everything back.
                    reparented.forEach((n) => {
                        // n.parent = null;
                        n.setParent(null);
                    });

                    root.dispose(true, true);

                    // Restart rendering loop.
                    this.startRenderLoop();

                    this.logMessage({
                        level: 'debug',
                        message: 'Restarted rendering...',
                    });

                    if (gltf.glTFFiles && gltf.glTFFiles['preview.glb']) {
                        const content = gltf.glTFFiles['preview.glb'];

                        let isSafari = navigator.userAgent.indexOf('Safari') >= 0 && navigator.userAgent.indexOf('Chrome') < 0;

                        let isAndroid = navigator.userAgent.indexOf('Android') >= 0;

                        if (window.location.search && window.location.search.includes('customizer-three-usdz=true')) {
                            isSafari = true;
                            isAndroid = false;
                        }

                        if (window.location.search && window.location.search.includes('customizer-three-glb=true')) {
                            isAndroid = true;
                            isSafari = false;
                        }

                        if (window.location.search && window.location.search.includes('customizer-three-raw=true')) {
                            isAndroid = false;
                            isSafari = false;
                        }

                        if (isSafari) {
                            const reader = new FileReader();

                            reader.onloadend = () => {
                                const base64Data = reader.result;

                                // Load generates GLTF into ThreeJS.
                                const loader = new ThreeGLTFLoader();

                                loader.load(base64Data, (threeGltf) => {
                                    // eslint-disable-next-line
                                    console.log('Re-loaded GLTF');

                                    const exporter = new ThreeUSDZExporter();

                                    const parseOptions = {};

                                    if (this.controller.blueprint.custom['three-ar-flip-winding'] === 'true') {
                                        parseOptions.flipWinding = true;
                                    }

                                    if (this.controller.blueprint.custom['three-ar-rotate-y']) {
                                        parseOptions.rotateY = parseFloat(this.controller.blueprint.custom['three-ar-rotate-y']);
                                    }

                                    if (this.controller.blueprint.custom['three-ar-rotate-x']) {
                                        parseOptions.rotateX = parseFloat(this.controller.blueprint.custom['three-ar-rotate-x']);
                                    }

                                    if (this.controller.blueprint.custom['three-ar-rotate-z']) {
                                        parseOptions.rotateZ = parseFloat(this.controller.blueprint.custom['three-ar-rotate-z']);
                                    }

                                    exporter.parse(threeGltf.scene, parseOptions).then((arraybuffer) => {
                                        const blob = new Blob([arraybuffer], { type: 'model/vnd.usdz+zip' });

                                        if (window.location.search && window.location.search.includes('customizer-three-save-usdz=true')) {
                                            saveAs(blob, 'preview.usdz');

                                            this.emitter.$emit('arPreviewDone');
                                        } else {
                                            const url = URL.createObjectURL(blob);

                                            const anchor = document.createElement('a');
                                            anchor.setAttribute('rel', 'ar');
                                            anchor.appendChild(document.createElement('img'));
                                            anchor.setAttribute('href', url);
                                            anchor.click();

                                            this.emitter.$emit('arPreviewDone');
                                        }
                                    });
                                }, undefined, (error) => {
                                    // eslint-disable-next-line
                                    console.error(error);
                                });
                            };

                            reader.readAsDataURL(content);
                        } else if (isAndroid) {
                            const formData = new FormData();

                            formData.append('file', content, 'preview.glb');

                            axios({
                                method: 'post',
                                url: '//reflector.customizer.drivecommerce.com/api/v1/data',
                                data: formData,
                                config: { headers: { 'Content-Type': 'multipart/form-data' } },
                            }).then((uploadResponse) => {
                                const { name } = uploadResponse.data;
                                const glbUrl = `https://reflector.customizer.drivecommerce.com/api/v1/data/${name}`;

                                // eslint-disable-next-line
                                const url = `intent://arvr.google.com/scene-viewer/1.0?file=${glbUrl}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;end;`;

                                const anchor = document.createElement('a');
                                anchor.setAttribute('href', url);
                                anchor.click();

                                // this.$store.dispatch('arPreviewDone');
                            });
                        } else {
                            this.logMessage({
                                level: 'debug',
                                message: 'Downloading GLB...',
                            });

                            saveAs(content, 'preview.glb');

                            // this.$store.dispatch('arPreviewDone');
                        }
                    }
                });
            }
        },
    },
};
